import React, { useState } from 'react';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import api from '../services/api';

export default function AprobarEvidencia({ evidencia, onAprobacionCompletada, onCancelar }) {
    const [aprobado, setAprobado] = useState(null);
    const [comentarios, setComentarios] = useState('');
    const [subiendo, setSubiendo] = useState(false);
    const [error, setError] = useState(null);

    const manejarAprobacion = async (esAprobado) => {
        setAprobado(esAprobado);
    };

    const confirmarAprobacion = async () => {
        if (aprobado === null) {
            setError('Selecciona una opción de aprobación');
            return;
        }

        setSubiendo(true);
        setError(null);

        try {
            await api.post(`/EvidenciaSeguridad/${evidencia.id}/aprobar`, {
                aprobado: aprobado,
                comentariosAprobacion: comentarios.trim() || null,
            });

            if (onAprobacionCompletada) {
                onAprobacionCompletada();
            }
        } catch (error) {
            console.error('Error al aprobar evidencia:', error);
            setError(error.response?.data?.message || error.message || 'Error al procesar la aprobación');
        } finally {
            setSubiendo(false);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-2xl w-full mx-4">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {aprobado === null ? 'Aprobar/Rechazar Evidencia' :
                        aprobado ? 'Confirmar Aprobación' : 'Confirmar Rechazo'}
                </h3>
            </div>

            <div className="p-6">
                {error && (
                    <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                        <div className="flex">
                            <AlertCircle className="h-5 w-5 text-red-400" />
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800 dark:text-red-400">Error</h3>
                                <div className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Información de la evidencia */}
                <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-2">{evidencia.codigoControl}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">{evidencia.descripcionControl}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                        <span>Prioridad: {evidencia.prioridad}</span>
                        <span>Tipo: {evidencia.tipo}</span>
                        <span>Imágenes: {evidencia.imagenes?.length || 0}</span>
                    </div>
                </div>

                {/* Opciones de aprobación */}
                {aprobado === null && (
                    <div className="mb-6">
                        <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">¿Qué acción deseas realizar?</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <button
                                onClick={() => manejarAprobacion(true)}
                                className="flex items-center justify-center space-x-2 p-4 border-2 border-green-200 dark:border-green-800 rounded-lg hover:border-green-300 dark:hover:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                            >
                                <CheckCircle className="h-6 w-6 text-green-600" />
                                <div className="text-left">
                                    <div className="font-medium text-green-900">Aprobar</div>
                                    <div className="text-sm text-green-600">La evidencia cumple con los requisitos</div>
                                </div>
                            </button>

                            <button
                                onClick={() => manejarAprobacion(false)}
                                className="flex items-center justify-center space-x-2 p-4 border-2 border-red-200 dark:border-red-800 rounded-lg hover:border-red-300 dark:hover:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            >
                                <XCircle className="h-6 w-6 text-red-600" />
                                <div className="text-left">
                                    <div className="font-medium text-red-900">Rechazar</div>
                                    <div className="text-sm text-red-600">La evidencia no cumple con los requisitos</div>
                                </div>
                            </button>
                        </div>
                    </div>
                )}

                {/* Confirmación */}
                {aprobado !== null && (
                    <div className="mb-6">
                        <div className={`p-4 rounded-lg border-2 ${aprobado ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                            }`}>
                            <div className="flex items-center space-x-2">
                                {aprobado ? (
                                    <CheckCircle className="h-5 w-5 text-green-600" />
                                ) : (
                                    <XCircle className="h-5 w-5 text-red-600" />
                                )}
                                <span className={`font-medium ${aprobado ? 'text-green-900' : 'text-red-900'
                                    }`}>
                                    {aprobado ? 'Aprobar Evidencia' : 'Rechazar Evidencia'}
                                </span>
                            </div>
                            <p className={`mt-2 text-sm ${aprobado ? 'text-green-700' : 'text-red-700'
                                }`}>
                                {aprobado
                                    ? 'Esta evidencia será marcada como aprobada y el control como cumplido.'
                                    : 'Esta evidencia será marcada como rechazada y requerirá corrección.'
                                }
                            </p>
                        </div>
                    </div>
                )}

                {/* Comentarios */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Comentarios {aprobado === false ? '(Requerido para rechazo)' : '(Opcional)'}
                    </label>
                    <textarea
                        value={comentarios}
                        onChange={(e) => setComentarios(e.target.value)}
                        placeholder={
                            aprobado === false
                                ? 'Explica por qué se rechaza la evidencia y qué se debe corregir...'
                                : 'Agrega comentarios adicionales sobre la aprobación...'
                        }
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required={aprobado === false}
                    />
                </div>

                {/* Botones de acción */}
                <div className="flex justify-end space-x-3">
                    <button
                        onClick={onCancelar}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        Cancelar
                    </button>

                    {aprobado !== null && (
                        <button
                            onClick={() => setAprobado(null)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            Volver
                        </button>
                    )}

                    {aprobado !== null && (
                        <button
                            onClick={confirmarAprobacion}
                            disabled={subiendo || (aprobado === false && !comentarios.trim())}
                            className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${aprobado
                                ? 'bg-green-600 hover:bg-green-700'
                                : 'bg-red-600 hover:bg-red-700'
                                }`}
                        >
                            {subiendo ? 'Procesando...' :
                                aprobado ? 'Confirmar Aprobación' : 'Confirmar Rechazo'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
